import { useState, useCallback, useContext, ReactElement } from 'react';
import styled from 'styled-components';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { Tooltip, Typography } from '@mui/material';
import { Flex, FlexColumn } from '../../Flex';
import IconButton from '../../IconButton';
import WidgetButton from '../../WidgetButton';
import { TrackingContext } from '../../../providers/tracking';
import {
  OnUserInteraction,
  ToolInvocationEvent,
  ToolRendererFunction,
} from '../types';
import { downloadTableAsCSV } from '../utils';
import FEToolRenderer, {
  hasFEToolRenderer,
  isFEToolExpandable,
} from './FEToolRenderer';
import { getApprovalToolRenderer } from './ApprovalToolRenderer';
import ToolCallChip from './ToolCallChip';
import { ToolText } from './toolDisplay';

const ICON_AREA_WIDTH = 32;

const ToolFullWidth = styled.div`
  width: 90cqi;
  margin-left: calc((100% - ${ICON_AREA_WIDTH}px - 90cqi) / 2);
  margin-right: calc((100% + ${ICON_AREA_WIDTH}px - 90cqi) / 2);
`;

type FEToolSectionProps = {
  invocation: ToolInvocationEvent['toolInvocation'];
  contentMaxWidth?: number;
  onUserInteraction?: OnUserInteraction;
  isToolLoading: boolean;
};

const FEToolSection = ({
  invocation,
  contentMaxWidth,
  onUserInteraction,
  isToolLoading,
}: FEToolSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const tracking = useContext(TrackingContext);

  const isExpandable = isFEToolExpandable(invocation.toolName || '');
  const isTable = invocation.toolName === 'table_display';
  const showToolbar = isExpandable && (isTable || !!contentMaxWidth);

  const handleDownload = () => {
    downloadTableAsCSV(
      invocation.args?.rows || [],
      invocation.args?.columns || [],
    );
    tracking?.sendTrackingEvent({
      eventName: tracking.analytics.events.AI_CHAT_TABLE_CSV_DOWNLOAD,
    });
  };

  const content = (
    <>
      {showToolbar && (
        <Flex sx={{ justifyContent: 'flex-end', mb: '4px' }}>
          {isTable && (
            <Tooltip title="Export CSV">
              <IconButton onClick={handleDownload}>
                <DownloadRoundedIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          )}
          {!!contentMaxWidth && (
            <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
              <IconButton onClick={() => setIsExpanded((prev) => !prev)}>
                {isExpanded ? (
                  <CloseFullscreenRoundedIcon sx={{ fontSize: '16px' }} />
                ) : (
                  <OpenInFullRoundedIcon sx={{ fontSize: '16px' }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Flex>
      )}
      <FEToolRenderer
        toolName={invocation.toolName}
        toolArgs={invocation.args}
        onUserInteraction={onUserInteraction}
        selectedValue={invocation.result}
        isActive={isToolLoading}
      />
    </>
  );

  if (isExpandable && isExpanded && contentMaxWidth) {
    return <ToolFullWidth>{content}</ToolFullWidth>;
  }

  return content;
};

type Props = {
  invocation: ToolInvocationEvent['toolInvocation'];
  isDebug?: boolean;
  onApproveToolUse?: (
    val: boolean,
    interruptId?: string,
    newArgs?: Record<string, unknown>,
  ) => void;
  onUserInteraction?: OnUserInteraction;
  getToolRenderer?: (toolName?: string) => ToolRendererFunction | null;
  contentMaxWidth?: number;
};

const ToolInvocationRenderer = ({
  invocation,
  isDebug = false,
  onApproveToolUse,
  onUserInteraction,
  getToolRenderer,
  contentMaxWidth,
}: Props) => {
  const [userResponded, setUserResponded] = useState(false);

  const wrapFullWidth = (element: ReactElement) =>
    contentMaxWidth ? <ToolFullWidth>{element}</ToolFullWidth> : element;

  const isToolLoading = invocation.state === 'call';
  const isToolError = invocation.result?.isError;
  const isToolApproval = invocation.type === 'approval';
  const isFETool = hasFEToolRenderer(invocation.toolName);
  const isStartEvent = invocation.type === 'start';

  const approveWithInterruptId = useCallback(
    (approved: boolean, newArgs?: Record<string, unknown>) =>
      onApproveToolUse?.(approved, invocation.interruptId, newArgs),
    [onApproveToolUse, invocation.interruptId],
  );

  const tryRenderWithToolRenderer = (
    renderer: ToolRendererFunction | null,
  ): ReactElement | null => {
    if (!renderer) {
      return null;
    }
    return (
      renderer(
        invocation.toolName || '',
        invocation.result?.result,
        invocation.args,
        isToolLoading,
        onUserInteraction,
      ) || null
    );
  };

  if (isDebug) {
    return (
      <FlexColumn>
        <Flex sx={{ gap: 1 }}>
          <Typography sx={{ fontWeight: 'bold' }}>
            {invocation.toolName}
          </Typography>

          {isToolLoading && <Typography>Running...</Typography>}
        </Flex>

        <FlexColumn>
          <Flex sx={{ gap: 1 }}>
            <Typography sx={{ fontWeight: 'bold' }}>Input:</Typography>

            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(invocation.args, null, 2)}
            </Typography>
          </Flex>

          <Flex sx={{ gap: 1 }}>
            <Typography sx={{ fontWeight: 'bold' }}>Output:</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
              {typeof invocation.result === 'object'
                ? JSON.stringify(invocation.result, null, 2)
                : String(invocation.result)}
            </Typography>
          </Flex>
        </FlexColumn>
      </FlexColumn>
    );
  }

  const customApprovalRenderer = getApprovalToolRenderer(invocation.toolName);
  if (customApprovalRenderer && !isStartEvent) {
    const rendered = customApprovalRenderer({
      invocation,
      onApproveToolUse: approveWithInterruptId,
      onUserInteraction,
      isActive: isToolLoading,
    });
    if (rendered) {
      return rendered;
    }
  }

  if (isToolApproval) {
    return (
      <Flex sx={{ gap: 1, alignItems: 'center' }}>
        <ToolText>{invocation.message || 'Do you approve tool use?'}</ToolText>
        <WidgetButton
          sx={{ minWidth: 0 }}
          variant="outlined"
          disabled={userResponded}
          onClick={() => {
            onApproveToolUse?.(true, invocation.interruptId);
            setUserResponded(true);
          }}
        >
          Yes
        </WidgetButton>
        <WidgetButton
          sx={{ minWidth: 0 }}
          variant="outlined"
          disabled={userResponded}
          onClick={() => {
            onApproveToolUse?.(false, invocation.interruptId);
            setUserResponded(true);
          }}
        >
          No
        </WidgetButton>
      </Flex>
    );
  }

  if (!isStartEvent && getToolRenderer) {
    const rendered = tryRenderWithToolRenderer(
      getToolRenderer(invocation.toolName),
    );
    if (rendered) {
      return wrapFullWidth(rendered);
    }
  }

  if (isToolError) {
    return (
      <Flex sx={{ alignItems: 'center', gap: 1 }}>
        <ErrorRoundedIcon sx={{ fontSize: '18px', color: 'common.magenta' }} />
        <ToolText>Tool call failure</ToolText>
      </Flex>
    );
  }

  if (isFETool && !isStartEvent) {
    return (
      <FEToolSection
        invocation={invocation}
        contentMaxWidth={contentMaxWidth}
        onUserInteraction={onUserInteraction}
        isToolLoading={isToolLoading}
      />
    );
  }

  if (getToolRenderer) {
    const rendered = tryRenderWithToolRenderer(getToolRenderer());
    if (rendered) {
      return wrapFullWidth(rendered);
    }
  }

  if (isFETool || isStartEvent) {
    const toolText = isFETool
      ? isToolLoading
        ? 'Thinking...'
        : 'Done!'
      : (invocation.args?.goal as string | undefined) ?? null;

    return (
      <Flex sx={{ alignItems: 'center', gap: 1 }}>
        {isToolLoading ? (
          <SearchRoundedIcon
            sx={{ fontSize: '18px', color: 'common.magenta' }}
          />
        ) : (
          <CheckCircleRoundedIcon
            sx={{ fontSize: '18px', color: 'common.magenta' }}
          />
        )}
        {toolText && <ToolText $running={isToolLoading}>{toolText}</ToolText>}
      </Flex>
    );
  }

  return <ToolCallChip invocation={invocation} />;
};

export default ToolInvocationRenderer;
